import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(() => localStorage.getItem("bb_token") || null);
    const [user, setUser] = useState(() => {
        const raw = localStorage.getItem("bb_user");
        try { return raw ? JSON.parse(raw) : null; } catch { return null; }
    });

    useEffect(() => {
        if (token) {
            localStorage.setItem("bb_token", token);
            api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        } else {
            localStorage.removeItem("bb_token");
            delete api.defaults.headers.common["Authorization"];
        }
    }, [token]);

    useEffect(() => {
        if (user) localStorage.setItem("bb_user", JSON.stringify(user));
        else localStorage.removeItem("bb_user");
    }, [user]);

    // Validate token on mount
    useEffect(() => {
        if (!token) return;
        api.get("/auth/me")
            .then((r) => setUser(r.data))
            .catch(() => {
                setToken(null);
                setUser(null);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkPhone = useCallback(async (phone) => {
        const r = await api.post("/auth/check-phone", { phone });
        return r.data; // { phone, exists, has_password }
    }, []);

    const sendOTP = useCallback(async (phone, purpose = "register") => {
        const r = await api.post("/auth/send-otp", { phone, purpose });
        return r.data; // { ok, phone, purpose, dev_otp? }
    }, []);

    const register = useCallback(async (phone, code, password, name) => {
        const r = await api.post("/auth/register", { phone, code, password, name });
        setToken(r.data.token);
        setUser(r.data.user);
        return r.data;
    }, []);

    const login = useCallback(async (phone, password) => {
        const r = await api.post("/auth/login", { phone, password });
        setToken(r.data.token);
        setUser(r.data.user);
        return r.data;
    }, []);

    const resetPassword = useCallback(async (phone, code, newPassword) => {
        const r = await api.post("/auth/reset-password", {
            phone, code, new_password: newPassword,
        });
        setToken(r.data.token);
        setUser(r.data.user);
        return r.data;
    }, []);

    // Legacy — kept for any older flow still using OTP-only login (unused after this iteration)
    const verifyOTP = useCallback(async (phone, code) => {
        const r = await api.post("/auth/verify-otp", { phone, code });
        setToken(r.data.token);
        setUser(r.data.user);
        return r.data;
    }, []);

    const logout = useCallback(() => {
        setToken(null);
        setUser(null);
        // Notify other contexts (CartContext, etc.) to reset themselves
        window.dispatchEvent(new CustomEvent("bazaar:logout"));
    }, []);

    const value = useMemo(
        () => ({
            user, token, setUser,
            checkPhone, sendOTP, register, login, resetPassword,
            verifyOTP, logout,
            isAuthed: !!token,
        }),
        [user, token, checkPhone, sendOTP, register, login, resetPassword, verifyOTP, logout],
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
};
